import * as React from 'react';
import { Select, SelectProps } from 'antd';
import { SelectValue } from 'antd/lib/select';
import { useDropdownTextSelection, useDropdownOpenState } from '../../hooks';
import { CustomInputLabel, CustomModifiedMarker } from '..';
import { ArrowDown } from '../../icons';
import { Utils } from '../../misc';
import './CustomSelect.less';

type CustomProps = {
    label: string;
    labelTooltip?: string;
    required?: boolean;
    topRightNode?: React.ReactNode;
    containerStyle?: React.CSSProperties;
    hasError?: boolean;
    showModifiedMarker?: boolean;
    getContainerRef?: (containerRef: React.RefObject<HTMLDivElement>) => void;
};

export type CustomSelectProps = CustomProps & Omit<SelectProps<SelectValue>, 'mode' | 'placeholder'>;

const CustomSelect: React.FC<CustomSelectProps> = (props: CustomSelectProps) => {
    const {
        open,
        label,
        labelTooltip,
        required,
        topRightNode,
        containerStyle,
        hasError,
        showModifiedMarker,
        getContainerRef,
        onPopupScroll,
        onMouseDown,
        onDropdownVisibleChange,
        ...selectProps
    } = props;

    const containerRef = React.useRef<HTMLDivElement>(null);

    const { clearTextSelection, handleTextSelection, renderTextSelectionButton } =
        useDropdownTextSelection(containerRef);

    const { isDropdownOpen, setIsDropdownOpen } = useDropdownOpenState(open);

    React.useEffect(() => {
        if (getContainerRef) {
            getContainerRef(containerRef);
        }
    }, [getContainerRef]);

    const getContainerClassName = () => {
        let className = 'custom-select-container';

        if (hasError) {
            className += ' has-error';
        }

        return className;
    };

    const getSelectClassName = () => {
        let className = 'custom-select';

        if (Utils.hasValue(selectProps.value)) {
            className += ' not-empty';
        }

        return className;
    };

    return (
        <div
            ref={containerRef}
            className={getContainerClassName()}
            style={containerStyle}
            onContextMenu={event => event.preventDefault()}
        >
            {!!topRightNode && <div className="top-right-node-container">{topRightNode}</div>}

            <Select
                open={isDropdownOpen}
                className={getSelectClassName()}
                suffixIcon={<ArrowDown />}
                dropdownClassName="custom-select-dropdown"
                onDropdownVisibleChange={isOpen => {
                    setIsDropdownOpen(isOpen);
                    onDropdownVisibleChange?.(isOpen);
                    clearTextSelection();
                }}
                onMouseDown={event => {
                    onMouseDown?.(event);
                    handleTextSelection(event);
                }}
                onPopupScroll={event => {
                    onPopupScroll?.(event);
                    clearTextSelection();
                }}
                {...selectProps}
            />

            {renderTextSelectionButton()}

            <CustomInputLabel label={label} labelTooltip={labelTooltip} required={required} />

            {showModifiedMarker && <CustomModifiedMarker />}
        </div>
    );
};

export default CustomSelect;
